// SK Hire design tokens — PURE BLACK & WHITE, matching the design handoff
// prototype (design_handoff_sk_hire/SK Hire.dc.html) exactly.
//
// The prototype's palette is 100% achromatic: every token is `oklch(L 0 0)`
// (chroma 0, hue 0) — meaning is carried by LIGHTNESS ONLY, never by hue.
// These hex values are the exact sRGB conversions of the prototype's oklch
// lightness values (RN StyleSheet has no oklch()).
//
// The `*Soft` / `*Line` / `*Ink` naming is load-bearing: screens derive tones
// dynamically via colors[`${tone}Soft`] etc. — keep the key names stable.
// The semantic families (info/success/warn/danger/purple/teal/neutral) are all
// greys at distinct lightnesses — do NOT reintroduce any hue.

export const LightColors = {
  // Surfaces — prototype oklch 0.985 / 0.965 / 0.935 / #fff
  paper:   '#FAFAFA',
  paper2:  '#F3F3F3',
  paper3:  '#E9E9E9',
  card:    '#FFFFFF',

  // Ink (text) — prototype oklch 0.17 / 0.34 / 0.52 / 0.70
  ink:     '#0F0F0F',
  ink2:    '#383838',
  ink3:    '#696969',
  ink4:    '#9E9E9E',

  // Lines — prototype oklch 0.90 / 0.79
  line:        '#DEDEDE',
  lineStrong:  '#BABABA',

  // Accent — prototype oklch 0.25 / 0.20 / 0.93 / 0.80 (dark grey, NOT a hue)
  accent:      '#222222',
  accentInk:   '#161616',
  accentSoft:  '#E8E8E8',
  accentLine:  '#BEBEBE',
  // Foreground for solid accent/success/danger fills. Light: white on dark fills.
  onAccent:    '#FFFFFF',

  // Info — in-progress middle stages, "Submitted" status (oklch 0.45 / 0.935)
  info:      '#555555',
  infoSoft:  '#E9E9E9',
  infoLine:  '#BEBEBE',

  // Success — placed, active consent, rerank ok (oklch 0.38 / 0.925)
  success:      '#424242',
  successSoft:  '#E6E6E6',
  successLine:  '#BEBEBE',

  // Warn — on hold, expiring consent, fallback rerank (oklch 0.55 / 0.93 / 0.78)
  warn:      '#717171',
  warnSoft:  '#E8E8E8',
  warnLine:  '#B7B7B7',

  // Danger — reject, expired, erased, no-credits (oklch 0.28 / 0.915 / 0.76)
  danger:      '#292929',
  dangerSoft:  '#E3E3E3',
  dangerLine:  '#B1B1B1',

  // Purple — Stage-3 score, interview stage, extraction confidence (oklch 0.47 / 0.935 / 0.83)
  purple:      '#5B5B5B',
  purpleSoft:  '#E9E9E9',
  purpleLine:  '#C7C7C7',

  // Teal — Stage-2 cosine (oklch 0.50 / 0.935 / 0.84)
  teal:      '#636363',
  tealSoft:  '#E9E9E9',
  tealLine:  '#CACACA',

  // Neutral — draft/new stage, withdrawn, in-house owner (oklch 0.52 / 0.94)
  neutral:      '#696969',
  neutralSoft:  '#EBEBEB',
  neutralLine:  '#CECECE',

  // Semantic aliases
  white: '#FFFFFF',
  black: '#000000',
  transparent: 'transparent',
};

// Dark theme — Linear-style, rebalanced for legibility: the canvas is lifted
// off pure black to reduce OLED glare and give surfaces a clearer sense of
// elevation, primary ink is a soft off-white (not harsh pure white), and the
// muted/faint inks + hairlines are brightened so secondary text and borders
// are actually readable. Kept 100% achromatic (pure grayscale, no hue) to
// honour the black-&-white design system.
export const DarkColors = {
  paper:   '#0E0E10',   // page background — lifted off pure black
  paper2:  '#1A1A1D',   // hovered rows / insets
  paper3:  '#26262A',   // raised chips / badges
  card:    '#17171A',   // panels & cards — clear lift over the page

  ink:     '#ECECEE',   // primary text — soft off-white (less glare)
  ink2:    '#C2C2C8',   // secondary
  ink3:    '#9A9AA2',   // muted — brightened so it reads clearly
  ink4:    '#71717A',   // faint — brightened so it stays visible

  line:        '#2B2B30',   // hairline borders (visible but thin)
  lineStrong:  '#3E3E45',   // emphasized borders

  accent:      '#EBEBED',   // solid fills are light in dark mode
  accentInk:   '#F6F6F8',
  accentSoft:  '#232328',   // active row tint (a touch more visible)
  accentLine:  '#44444C',
  // Solid fills are LIGHT in dark mode → text/icons on them must be dark.
  onAccent:    '#0E0E10',

  info:      '#AEAEB5',
  infoSoft:  '#1F1F23',
  infoLine:  '#3A3A40',

  success:      '#BEBEC4',
  successSoft:  '#1F1F23',
  successLine:  '#3A3A40',

  warn:      '#98989F',
  warnSoft:  '#212125',
  warnLine:  '#3C3C42',

  danger:      '#CBCBD1',
  dangerSoft:  '#212125',
  dangerLine:  '#3E3E44',

  purple:      '#ADADB4',
  purpleSoft:  '#1F1F23',
  purpleLine:  '#3A3A40',

  teal:      '#A3A3AA',
  tealSoft:  '#1F1F23',
  tealLine:  '#3A3A40',

  neutral:      '#9E9EA5',
  neutralSoft:  '#1D1D21',
  neutralLine:  '#37373C',

  white: '#FFFFFF',
  black: '#000000',
  transparent: 'transparent',
};

export type ColorScheme = typeof LightColors;
